import { create } from 'zustand';

import { buildFinalCaption, buildMarkedCaption } from '../lib/derive.js';
import { remapSpans } from '../lib/remap.js';
import { collectSpans, findOverlap, operationFor } from '../lib/spans.js';

/**
 * Working state for the image currently open: one draft per caption.
 *
 * Kept in a store rather than component state so keyboard handlers can read fresh
 * values via `getState()` without re-subscribing, and so editing one caption's note
 * does not re-render the other two.
 */

function draftFromSample(sample) {
  const annotation = sample.annotation || {};
  const working = annotation.human_working_caption || sample.agent_final_caption || '';
  const final = annotation.human_final_caption || working;
  return {
    sampleId: sample.sample_id,
    slot: sample.slot,
    label: sample.label,
    agentCaption: sample.agent_final_caption || '',
    working,
    finalCaption: final,
    spans: (annotation.tag_spans || []).map((s) => ({ ...s })),
    addedSpans: annotation.added_text_spans || [],
    notes: annotation.notes || '',
    status: annotation.status || 'in_progress',
    version: annotation.version ?? 1,
    provenance: annotation.provenance || null,
    flags: annotation.flags || {},
    spanDecisions: annotation.span_decisions || [],
    detailDecisions: annotation.detail_decisions || [],
    dirty: false,
    // Set after a caption edit re-anchors the tags, so the UI can offer an undo.
    remapReport: null,
    undoSnapshot: null,
  };
}

export const useEditorStore = create((set, get) => ({
  taskId: null,
  ownerId: null,
  drafts: [],
  focusedSlot: 0,
  spanCursor: -1,
  cursorScroll: false,
  // {index, anchor} - the tag being edited in place
  spanPopover: null,
  selection: null,

  load(task, ownerId) {
    set({
      taskId: task.task.id,
      ownerId,
      drafts: (task.samples || []).map(draftFromSample),
      focusedSlot: 0,
      spanCursor: -1,
      cursorScroll: false,
      spanPopover: null,
      selection: null,
    });
  },

  reset() {
    set({ taskId: null, drafts: [], focusedSlot: 0, spanCursor: -1, selection: null });
  },

  focused: () => get().drafts[get().focusedSlot] || null,
  draftAt: (slot) => get().drafts[slot] || null,

  focusSlot(slot) {
    const { drafts } = get();
    if (slot < 0 || slot >= drafts.length) return;
    set({ focusedSlot: slot, spanCursor: -1, cursorScroll: false, spanPopover: null, selection: null });
  },

  cycleFocus(step) {
    const { focusedSlot, drafts } = get();
    if (!drafts.length) return;
    get().focusSlot((focusedSlot + step + drafts.length) % drafts.length);
  },

  setSelection: (selection) => set({ selection }),
  openSpanPopover: (index, anchor) => set({ spanPopover: { index, anchor }, spanCursor: index }),
  closeSpanPopover: () => set({ spanPopover: null }),

  /**
   * Walk the span cursor with the keyboard.
   *
   * `cursorScroll` records that the caption should follow. Clicking a mark or picking a
   * row in the rail sets the cursor WITHOUT it: those targets are already on screen, and
   * scrolling under the pointer makes the page feel like it is running away.
   */
  moveCursor(step) {
    const draft = get().focused();
    if (!draft || !draft.spans.length) return;
    const { spanCursor } = get();
    const next =
      spanCursor < 0
        ? step > 0
          ? 0
          : draft.spans.length - 1
        : (spanCursor + step + draft.spans.length) % draft.spans.length;
    set({ spanCursor: next, cursorScroll: true });
  },
  setCursor: (index, { scroll = false } = {}) => set({ spanCursor: index, cursorScroll: scroll }),

  /** Update one draft and mark it dirty; `patch` may be a function. */
  updateDraft(slot, patch) {
    set((state) => ({
      drafts: state.drafts.map((draft, i) => {
        if (i !== slot) return draft;
        const changes = typeof patch === 'function' ? patch(draft) : patch;
        return { ...draft, ...changes, dirty: true };
      }),
    }));
  },

  /** Tag the current selection. Returns the new span index, or null. */
  addTag(label) {
    const { focusedSlot, selection } = get();
    const draft = get().focused();
    if (!draft || !selection) return null;

    if (findOverlap(draft.spans, selection)) return null;

    const span = {
      start_char: selection.start_char,
      end_char: selection.end_char,
      text: selection.text,
      label,
      operation: operationFor(label),
      replacement_text: '',
      notes: '',
    };

    const spans = collectSpans([...draft.spans, span]);
    const index = spans.findIndex(
      (s) => s.start_char === span.start_char && s.end_char === span.end_char,
    );

    get().updateDraft(focusedSlot, (d) => ({
      spans,
      finalCaption: buildFinalCaption(d.working, spans),
    }));
    // Open the editor straight away: for a replacement the annotator's next action is
    // always to type the replacement, so put the caret there rather than making them
    // find the span again further down the page.
    const anchor = selection.rect
      ? {
          top: selection.rect.top, bottom: selection.rect.bottom,
          left: selection.rect.left, width: selection.rect.width,
        }
      : null;
    set({ selection: null, spanCursor: index, cursorScroll: true, spanPopover: { index, anchor } });
    window.getSelection()?.removeAllRanges();
    return index;
  },

  updateSpan(index, patch) {
    const { focusedSlot } = get();
    get().updateDraft(focusedSlot, (draft) => {
      const spans = draft.spans.map((span, i) => {
        if (i !== index) return span;
        const next = { ...span, ...patch };
        // Typing a replacement turns a hallucination into a `replace`; clearing it
        // turns it back into a `remove`. The annotator never picks the action.
        next.operation = operationFor(next.label, next);
        return next;
      });
      return { spans, finalCaption: buildFinalCaption(draft.working, spans) };
    });
  },

  retagSpan(index, label) {
    const draft = get().focused();
    if (!draft) return;
    const current = draft.spans[index];
    if (!current || current.label === label) return;

    // Drop whatever no longer applies: a replacement is meaningless on an uncertain
    // span, and a note is meaningless on a hallucination. Leaving them would let an
    // invalid combination survive a retag.
    const patch = { label };
    if (label !== 'hallucinated') patch.replacement_text = '';
    if (label !== 'uncertain') patch.notes = '';
    get().updateSpan(index, patch);
  },

  removeSpan(index) {
    const { focusedSlot, spanCursor } = get();
    const draft = get().focused();
    if (!draft) return null;
    const removed = draft.spans[index];

    get().updateDraft(focusedSlot, (d) => {
      const spans = d.spans.filter((_, i) => i !== index);
      return { spans, finalCaption: buildFinalCaption(d.working, spans) };
    });
    if (spanCursor >= index) set({ spanCursor: Math.max(-1, spanCursor - 1) });
    set({ spanPopover: null });
    return removed;
  },

  restoreSpan(span) {
    const { focusedSlot } = get();
    get().updateDraft(focusedSlot, (draft) => {
      const spans = collectSpans([...draft.spans, span]);
      return { spans, finalCaption: buildFinalCaption(draft.working, spans) };
    });
  },

  /**
   * Commit a free-text caption edit, re-anchoring the tags.
   *
   * The legacy version dropped un-relocatable spans silently. Here the outcome is
   * reported and the pre-edit state is kept so the annotator can undo.
   */
  commitCaptionEdit(slot, nextText) {
    const draft = get().draftAt(slot);
    if (!draft || nextText === draft.working) {
      return null;
    }

    const snapshot = { working: draft.working, spans: draft.spans, finalCaption: draft.finalCaption };
    const { spans, moved, dropped } = remapSpans(draft.spans, draft.working, nextText);

    get().updateDraft(slot, {
      working: nextText,
      spans,
      finalCaption: buildFinalCaption(nextText, spans),
      remapReport: moved.length || dropped.length ? { moved: moved.length, dropped } : null,
      undoSnapshot: snapshot,
    });
    set({ spanCursor: -1 });
    return { moved: moved.length, dropped: dropped.length };
  },

  undoCaptionEdit(slot) {
    const draft = get().draftAt(slot);
    if (!draft?.undoSnapshot) return;
    get().updateDraft(slot, {
      ...draft.undoSnapshot,
      remapReport: null,
      undoSnapshot: null,
    });
  },

  dismissRemapReport(slot) {
    get().updateDraft(slot, { remapReport: null });
  },

  setFinalCaption(slot, text) {
    get().updateDraft(slot, { finalCaption: text });
  },

  setDecision(slot, spanIndex, decision) {
    get().updateDraft(slot, (draft) => {
      const existing = draft.spanDecisions.filter((d) => d.span_index !== spanIndex);
      // Clicking the current verdict again clears it back to pending (= no row).
      const current = draft.spanDecisions.find((d) => d.span_index === spanIndex);
      if (current?.decision === decision) return { spanDecisions: existing };
      return {
        spanDecisions: [...existing, { span_index: spanIndex, decision, human_notes: current?.human_notes || '' }],
      };
    });
  },

  /** Human notes on one pipeline decision, for either queue kind. */
  setDecisionNote(slot, item, note) {
    get().updateDraft(slot, (draft) => {
      if (item.kind === 'span') {
        const existing = draft.spanDecisions.find((d) => d.span_index === item.index);
        const rest = draft.spanDecisions.filter((d) => d.span_index !== item.index);
        return {
          spanDecisions: [
            ...rest,
            { ...(existing || { span_index: item.index, decision: 'confirmed' }), human_notes: note },
          ],
        };
      }
      const existing = draft.detailDecisions.find((d) => d.detail_index === item.index);
      const rest = draft.detailDecisions.filter((d) => d.detail_index !== item.index);
      return {
        detailDecisions: [
          ...rest,
          {
            ...(existing || { detail_index: item.index, decision: 'confirmed', edited_detail: '' }),
            human_notes: note,
          },
        ],
      };
    });
  },

  setDetailDecision(slot, detailIndex, decision) {
    get().updateDraft(slot, (draft) => {
      const existing = draft.detailDecisions.filter((d) => d.detail_index !== detailIndex);
      const current = draft.detailDecisions.find((d) => d.detail_index === detailIndex);
      if (current?.decision === decision) return { detailDecisions: existing };
      return {
        detailDecisions: [
          ...existing,
          { detail_index: detailIndex, decision, edited_detail: current?.edited_detail || '', human_notes: '' },
        ],
      };
    });
  },

  markSaved(results) {
    set((state) => ({
      drafts: state.drafts.map((draft) => {
        const result = results.find((r) => r.annotation_id && r.document?.sample_id === draft.sampleId);
        if (!result) return draft;
        return {
          ...draft,
          dirty: false,
          version: result.version,
          provenance: result.document?.provenance || draft.provenance,
          addedSpans: result.document?.added_text_spans || draft.addedSpans,
        };
      }),
    }));
  },

  /**
   * Payload for the batch save endpoint - only the captions that actually changed.
   *
   * `isAdmin` gates the decision arrays. The server refuses `span_decisions` from an
   * annotator with 403 (those are an admin surface), so sending even an empty array
   * would break every annotator save.
   */
  buildPayload(isAdmin = false) {
    return get()
      .drafts.filter((d) => d.dirty)
      .map((draft) => ({
        sample_id: draft.sampleId,
        base_version: draft.version,
        client_request_id: `${draft.sampleId}-${draft.version}-${Date.now().toString(36)}`,
        human_working_caption: draft.working,
        human_final_caption: draft.finalCaption,
        human_marked_caption_with_edits: buildMarkedCaption(draft.working, draft.spans),
        notes: draft.notes,
        ...(isAdmin
          ? {
              span_decisions: draft.spanDecisions,
              detail_decisions: draft.detailDecisions,
            }
          : {}),
        tag_spans: draft.spans.map((s) => ({
          start_char: s.start_char,
          end_char: s.end_char,
          text: s.text,
          label: s.label,
          operation: s.operation,
          replacement_text: s.replacement_text || '',
          notes: s.notes || '',
        })),
      }));
  },

  hasUnsaved: () => get().drafts.some((d) => d.dirty),
}));
